
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProfileHeader from "@/components/profile/ProfileHeader";
import ProfileTabs from "@/components/profile/ProfileTabs";
import { UserProfile } from "@/contexts/auth/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Profile = () => {
  const { user, profile, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const deleteConfirmToken = searchParams.get('delete_confirm');
  const [activeTab, setActiveTab] = useState(tabParam || "profile");
  const [processingConfirmation, setProcessingConfirmation] = useState(false);

  // Handle deletion confirmation token
  useEffect(() => {
    const handleDeletionConfirmation = async () => {
      if (deleteConfirmToken && user && !processingConfirmation) {
        setProcessingConfirmation(true);
        
        try {
          console.log('Confirming deletion with token:', deleteConfirmToken);
          
          const { data, error } = await supabase.functions.invoke('confirm-user-deletion', {
            body: { token: deleteConfirmToken }
          });

          console.log('Confirmation response:', { data, error });

          if (error) {
            console.error('Confirmation error:', error);
            toast.error(`Confirmation failed: ${error.message}`);
          } else if (data?.success) {
            toast.success(data.message);
            // Set active tab to privacy to show updated status
            setActiveTab("privacy");
            // Force a refresh of the deletion status by updating the URL
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('delete_confirm');
            newParams.set('tab', 'privacy');
            setSearchParams(newParams, { replace: true });
          } else {
            console.error('Confirmation failed:', data);
            toast.error(data?.error || "Failed to confirm deletion");
          }
        } catch (err: any) {
          console.error("Error confirming deletion:", err);
          toast.error("Failed to confirm deletion request");
        } finally {
          // Remove the token from URL after processing
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('delete_confirm');
          if (newParams.toString()) {
            setSearchParams(newParams, { replace: true });
          } else {
            navigate('/profile', { replace: true });
          }
          setProcessingConfirmation(false);
        }
      }
    };

    handleDeletionConfirmation();
  }, [deleteConfirmToken, user, processingConfirmation, searchParams, setSearchParams, navigate]);

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab && activeTab !== "profile") {
      navigate(`/profile?tab=${activeTab}`, { replace: true });
    } else {
      navigate("/profile", { replace: true });
    }
  }, [activeTab, navigate]);

  // Set active tab from URL on initial load
  useEffect(() => {
    if (tabParam && ["profile", "demographics", "verification", "notifications", "howto", "security", "points", "privacy"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth");
    }
  }, [isLoading, user, navigate]);

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-purple"></div>
      </div>
    );
  }

  // Generate initials from user's name
  const getInitials = () => {
    if (profile?.name) {
      return profile.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    }
    return user.email?.substring(0, 2).toUpperCase() || "U";
  };

  // Create a user profile object to pass to components
  const userProfile: UserProfile = {
    email: user.email,
    profile: profile
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-[97px] pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <ProfileHeader user={userProfile} getInitials={getInitials} setActiveTab={setActiveTab} />
            <ProfileTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
